/**
 * P-007 c6 — connectivity detection module.
 *
 * Derives a three-state connectivity mode — ONLINE / DEGRADED / OFFLINE — from `navigator.onLine`,
 * the Network Information API (`navigator.connection`), and periodic reachability probes against a
 * lightweight backend endpoint. Hysteresis timers gate the transitions so a brief blip doesn't
 * flap the app between modes: N consecutive agreeing observations are required before a mode change
 * is emitted. Subscribers (the sync engine, the UI banner) listen via subscribe().
 */
export const MODE = { ONLINE: 'ONLINE', DEGRADED: 'DEGRADED', OFFLINE: 'OFFLINE' };

export class ConnectivityMonitor {
  constructor({ probeUrl = '/api/health', probeIntervalMs = 30000, hysteresis = 2, fetchImpl, nav } = {}) {
    this._probeUrl = probeUrl;
    this._probeIntervalMs = probeIntervalMs;
    this._hysteresis = hysteresis;
    this._fetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null);
    this._nav = nav || (typeof navigator !== 'undefined' ? navigator : { onLine: true });
    this._mode = this._nav.onLine ? MODE.ONLINE : MODE.OFFLINE;
    this._pending = null;      // candidate mode awaiting hysteresis confirmation
    this._pendingCount = 0;
    this._subs = new Set();
    this._timer = null;
  }

  get mode() { return this._mode; }

  subscribe(fn) { this._subs.add(fn); return () => this._subs.delete(fn); }

  _emit() { for (const fn of this._subs) { try { fn(this._mode); } catch (_) { /* isolate subscriber errors */ } } }

  /** Feed one raw observation through the hysteresis filter; commit + emit only on a confirmed change. */
  _observe(candidate) {
    if (candidate === this._mode) { this._pending = null; this._pendingCount = 0; return this._mode; }
    if (candidate === this._pending) {
      if (++this._pendingCount >= this._hysteresis) {
        this._mode = candidate; this._pending = null; this._pendingCount = 0; this._emit();
      }
    } else {
      this._pending = candidate; this._pendingCount = 1;
    }
    return this._mode;
  }

  /** Classify current conditions into a candidate mode from onLine + connection + a probe. */
  async evaluate() {
    if (!this._nav.onLine) return this._observe(MODE.OFFLINE);
    const conn = this._nav.connection;
    // Network Information API says effectively no/near-no connectivity → DEGRADED before we even probe.
    if (conn && (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g' || conn.saveData === true)) {
      const reachable = await this._probe();
      return this._observe(reachable ? MODE.DEGRADED : MODE.OFFLINE);
    }
    const reachable = await this._probe();
    return this._observe(reachable ? MODE.ONLINE : MODE.DEGRADED);
  }

  async _probe() {
    if (!this._fetch) return this._nav.onLine;
    try {
      const res = await this._fetch(this._probeUrl, { method: 'GET', cache: 'no-store' });
      // Always consume the body: an unread no-store reply never finishes loading in Chromium (it stays "in flight"
      // for the life of the page). Proven on EC2Dev 2026-10-22 (D11).
      if (res && typeof res.text === 'function') await res.text().catch(() => {});
      return !!(res && (res.ok || res.status === 200));
    } catch (_) {
      return false;
    }
  }

  /** Start listening to browser online/offline events + periodic probing. Returns a stop() fn. */
  start() {
    if (typeof globalThis.addEventListener === 'function') {
      this._onOnline = () => this.evaluate();
      this._onOffline = () => this._observe(MODE.OFFLINE) === MODE.OFFLINE && this._emitIfChanged(MODE.OFFLINE);
      globalThis.addEventListener('online', this._onOnline);
      globalThis.addEventListener('offline', () => { this._mode = MODE.OFFLINE; this._pending = null; this._emit(); });
    }
    this.evaluate();
    this._timer = setInterval(() => this.evaluate(), this._probeIntervalMs);
    return () => this.stop();
  }

  _emitIfChanged() { /* offline is immediate-commit; kept for symmetry */ }

  stop() {
    if (this._timer) clearInterval(this._timer);
    this._timer = null;
    if (typeof globalThis.removeEventListener === 'function') {
      globalThis.removeEventListener('online', this._onOnline);
    }
  }
}
